// Het tabblad Instellingen van Configuratie (W6 ronde 6; research §3.7, besluit V5).
//
// V5: drie soorten waarden stelt de beheerder hier zelf vast — het kostenplafond,
// de dekkingsdoelen en het standaardkrediet per rol. De yaml blijft de
// standaard; een vastgelegde waarde gaat daar bij het lezen overheen. Per
// waarde staan ze naast elkaar: de standaard (yaml) en wat nu geldt, met wie het
// vaststelde, wanneer en waarom.
//
// Opslaan SCHRIJFT in de gedeelde database (tabel instellingen, migratie 0033;
// alleen toevoegen, de laatste rij wint). Daarom:
//  · één opslag tegelijk: tijdens het opslaan staan alle formulieren dicht, en
//    ze gaan pas weer open als het nieuwe antwoord van de server binnen is — zo
//    klikt niemand verder op een kaart die nog de oude waarde toont. "Dicht"
//    is hier aria-disabled en alleen-lezen, niet `disabled`: een knop of veld
//    dat zichzelf uitzet terwijl het de focus heeft, laat die op de pagina
//    vallen, en een toetsenbordgebruiker moet dan van boven af opnieuw zoeken;
//  · de server controleert (sof/instellingen.py#valideer) en het scherm bouwt
//    die controle niet na. Het bereik van de server staat onder het veld als
//    hint, niet als controle. Een 400 draagt een Nederlandse zin ("Kies een
//    getal van 0 tot en met 100.") en die staat er letterlijk;
//  · wat het scherm wél doet, is het getypte getal lezen zoals een Nederlander
//    het typt (`leesGetal`): de komma scheidt de decimalen, en punten tussen
//    groepen van drie cijfers zijn duizendtallen. Een getalveld van de browser
//    las "1.000" als 1 (en het plafond werd $1 — de server vindt dat goed) en
//    "12,5" in sommige browsers als leeg. Naast het veld staat wat er verstuurd
//    wordt ("Wordt opgeslagen als $1.000,00"): opmaak, geen controle;
//  · wat het scherm tegenhoudt is een leeg of onleesbaar veld. JSON kent geen
//    "geen getal": het zou als null aankomen, en bij een dekkingsdoel betekent
//    null "bewust geen doel". Een lege invoer mag dat nooit stil worden —
//    daarom is "geen doel" een eigen vinkje, en alleen waar de server `magLeeg`
//    zegt.
//
// De soort van een kredietwaarde (onderwerp, gesprek) heet hier zoals overal:
// `kredietLabel` uit woorden.ts. Eén as, één woordenlijst, één plek.
//
// Geen sleutels op het scherm ("kosten.plafond_usd", "krediet.directie.gesprek"):
// ze reizen mee naar de server, maar de beheerder leest het label.

import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent } from "react";

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason } from "../../components/Empty";
import { api } from "../../lib/api";
import type { ConfiguratieResponse, Instelling, InstellingRequest } from "../../contracts";
import { color, sectionLabel, size, weight } from "../../tokens";
import { Blok, cijfer, datum, getal, noot, procent } from "./BronnenStukken";
import { TerugIcoon, VinkIcoon } from "./Iconen";
import { cel, klein, kopCel, Tabel, usd } from "./KostenStukken";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { kredietLabel, leesbaar } from "./woorden";

type Recent = ConfiguratieResponse["recent"][number];

/** Slaat op; geeft de foutzin terug, of null als het lukte. */
type Opslaan = (
  item: Instelling,
  body: InstellingRequest,
  melding: (nieuw: Instelling) => string,
) => Promise<string | null>;

/** Een waarde van de server in zijn eenheid: "$150,00", "90%", "100". Alleen
 *  opmaak — de server stuurde het getal. */
export function inEenheid(n: number, eenheid: Instelling["eenheid"]): string {
  if (eenheid === "aanuit") return n ? "aan" : "uit";
  return eenheid === "usd" ? usd(n) : eenheid === "procent" ? procent(n) : getal(n);
}

/**
 * Een getal zoals een Nederlander het typt, of null als het niet eenduidig te
 * lezen is:
 *  · "150", "12,5"           — de komma scheidt de decimalen;
 *  · "1.000", "1.000,50"     — punten alleen tussen groepen van precies drie
 *                              cijfers: dan zijn het duizendtallen;
 *  · "1.5", "12.5", "1 000", "abc" — niet eenduidig, dus null ("Vul een getal
 *                              in."), en er gaat niets naar de server.
 * Alleen lezen, geen bereik: dat controleert de server.
 */
export function leesGetal(tekst: string): number | null {
  const m = /^(-?)(\d+|\d{1,3}(?:\.\d{3})+)(?:,(\d+))?$/.exec(tekst.trim());
  if (!m) return null;
  const heel = m[2]!.replace(/\./g, "");
  const n = Number(`${m[1]}${heel}${m[3] ? `.${m[3]}` : ""}`);
  return Number.isFinite(n) ? n : null;
}

/** Een getal van de server zoals het in het veld begint: met een komma en
 *  zonder duizendtallen (12.5 → "12,5", 1000 → "1000"), zodat `leesGetal` het
 *  ongewijzigd terugleest. */
function naarTekst(n: number): string {
  return n.toLocaleString("nl-NL", { useGrouping: false, maximumFractionDigits: 20 });
}

/** Wat er verstuurd wordt, precies: "$1.000,50", "12,5%", "150 gesprekken".
 *  Niet afgerond zoals op de kaart — anders las "12,55" als 12,6%. */
function precies(n: number, item: Instelling): string {
  const tal = (minDec: number) =>
    n.toLocaleString("nl-NL", { minimumFractionDigits: minDec, maximumFractionDigits: 20 });
  if (item.eenheid === "aanuit") return n ? "aan" : "uit";
  if (item.eenheid === "usd") return `$${tal(2)}`;
  if (item.eenheid === "procent") return `${tal(0)}%`;
  // Het woord van de soort is een meervoud ("gesprekken"): bij één niet.
  return item.soort && n !== 1 ? `${tal(0)} ${kredietLabel(item.soort).toLowerCase()}` : tal(0);
}

/** Is de focus kwijt (terug op de pagina zelf)? Alleen dan zet het scherm hem
 *  ergens neer: wie intussen verder klikte of van tabblad wisselde, houdt hem. */
export function focusKwijt(): boolean {
  const a = document.activeElement;
  return !a || a === document.body;
}

/** "Accountmanagement · Gesprekken": bij een kredietwaarde hoort de soort erbij,
 *  anders staan er twee kaarten met dezelfde naam. */
function naamVan(i: { label: string; soort: string | null }): string {
  return i.soort ? `${i.label} · ${kredietLabel(i.soort)}` : i.label;
}

/** Hoe "leeg" heet: bij een dekkingsdoel is leeg een besluit (geen doel). */
function leegWoord(item: Instelling): string {
  return item.magLeeg ? "geen doel" : "geen waarde";
}

/** De instellingen per groep, in de volgorde van de server. */
function perGroep(lijst: readonly Instelling[]): [string, Instelling[]][] {
  const groepen = new Map<string, Instelling[]>();
  for (const i of lijst) {
    const g = groepen.get(i.groepLabel);
    if (g) g.push(i);
    else groepen.set(i.groepLabel, [i]);
  }
  return [...groepen];
}

/** Opgeslagen, maar het nieuwe antwoord kwam niet: de kaart toont nog de oude
 *  waarde, dus "is nu $150,00" zou liegen. De knop staat bovenaan, bij de fout
 *  die de schil toont. */
const NIET_BIJGEWERKT =
  "Opgeslagen, maar de kaart kon niet worden bijgewerkt — kies bovenaan Opnieuw proberen.";

export function Instellingen({
  data,
  binnen,
  ophaalMislukt,
  herlaad,
}: {
  data: ConfiguratieResponse;
  /** Het volgnummer van de laatste vraag die terugkwam (gelukt of mislukt). */
  binnen: number;
  /** Mislukte die laatste vraag? Dan toont de kaart nog het vorige antwoord. */
  ophaalMislukt: boolean;
  /** Haalt opnieuw op; geeft het volgnummer van de laatste vraag daarvóór. */
  herlaad: () => number;
}) {
  const [bezig, setBezig] = useState<string | null>(null);
  const [melding, setMelding] = useState("");
  // Opgeslagen, maar het nieuwe antwoord is nog niet terug: `na` is de laatste
  // vraag van vóór het opslaan, `melding` wat er daarna gemeld wordt.
  const [wacht, setWacht] = useState<{ na: number; melding: string } | null>(null);
  // Opgeslagen, maar het ophalen daarna mislukte: de melding die hoort bij de
  // eerste ronde ná `na` die wél lukt (de knop Opnieuw proberen van de schil).
  const [achter, setAchter] = useState<{ na: number; melding: string } | null>(null);

  // Pas als een antwoord van ná het opslaan binnen is, gaan de formulieren
  // open. Lukte het ophalen, dan toont de kaart de nieuwe waarde en klinkt
  // "Opgeslagen: … is nu …". Mislukte het, dan staat de waarde wél maar de
  // kaart niet: dat zegt de melding, en pas een geslaagde nieuwe ronde (de
  // knop van de schil) brengt de gewone melding.
  useEffect(() => {
    if (wacht && binnen > wacht.na) {
      setWacht(null);
      setBezig(null);
      if (ophaalMislukt) {
        setMelding(NIET_BIJGEWERKT);
        setAchter({ na: binnen, melding: wacht.melding });
      } else {
        setMelding(wacht.melding);
      }
      return;
    }
    if (achter && !ophaalMislukt && binnen > achter.na) {
      setAchter(null);
      setMelding(achter.melding);
    }
  }, [wacht, achter, binnen, ophaalMislukt]);

  const opslaan: Opslaan = async (item, body, maakMelding) => {
    setBezig(item.sleutel);
    setAchter(null);
    setMelding("De instelling wordt opgeslagen…");
    let nieuw: Instelling;
    try {
      nieuw = await api.instelling(body);
    } catch (err) {
      setBezig(null);
      setMelding("");
      return leesbaar(err, "De instelling kon niet worden opgeslagen — probeer het zo nog eens.");
    }
    // De melding komt uit het antwoord van de server (de waarde zoals hij hem
    // vastlegde); de kaarten uit de nieuwe ronde.
    setWacht({ na: herlaad(), melding: maakMelding(nieuw) });
    return null;
  };

  const uit = bezig !== null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 26 }}>
      <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55, margin: 0 }}>
        Drie soorten waarden stel je hier zelf vast: het plafond, de dekkingsdoelen en het
        standaardkrediet per rol. De standaard staat in het configuratiebestand (yaml); wat je hier
        opslaat gaat daarvoor, voor iedereen. De server controleert elke waarde.
      </p>

      {/* Een levend gebied dat er altijd staat: een schermlezer meldt dan zowel
          "wordt opgeslagen" als het resultaat (zelfde afspraak als de
          steekproef op Kwaliteit). */}
      <div role="status" aria-live="polite" data-instelling-status>
        {melding && <p style={{ ...noot, marginTop: 0, color: color.body }}>{melding}</p>}
      </div>

      {data.instellingen.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason reason="de server stuurde geen instellingen" />
        </Card>
      ) : (
        perGroep(data.instellingen).map(([groep, items]) => (
          <Blok key={groep} titel={groep}>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 1fr))",
                gap: 12,
              }}
            >
              {items.map((item) => (
                <InstellingKaart
                  key={item.sleutel}
                  item={item}
                  uit={uit}
                  bezig={bezig === item.sleutel}
                  onOpslaan={opslaan}
                />
              ))}
            </ul>
          </Blok>
        ))
      )}

      <LaatstGewijzigd recent={data.recent} instellingen={data.instellingen} />
      <Verweesd rijen={data.verweesd} />
      <NietInstelbaar rijen={data.nietInstelbaar} />
    </div>
  );
}

/** Wie het vaststelde en wanneer, als zin; null als de standaard nooit werd aangeraakt. */
function herkomst(item: Instelling): string | null {
  if (!item.overschreven && !item.terugGezet) return null;
  const wie = item.door ? ` door ${item.door}` : "";
  const op = datum(item.op);
  const wanneer = op ? ` op ${op}` : "";
  return `${item.terugGezet ? "Teruggezet naar de standaard" : "Vastgesteld"}${wie}${wanneer}.`;
}

function beginTekst(item: Instelling): string {
  return item.waarde === null ? "" : naarTekst(item.waarde);
}

function InstellingKaart({
  item,
  uit,
  bezig,
  onOpslaan,
}: {
  item: Instelling;
  /** Er loopt ergens een opslag: alle formulieren dicht. */
  uit: boolean;
  /** De opslag van déze kaart loopt. */
  bezig: boolean;
  onOpslaan: Opslaan;
}) {
  const id = useId();
  const naam = naamVan(item);
  const [tekst, setTekst] = useState(() => beginTekst(item));
  const [geenDoel, setGeenDoel] = useState(item.magLeeg && item.waarde === null);
  const [motivering, setMotivering] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const opslaanKnop = useRef<HTMLButtonElement>(null);
  const terugFocus = useRef(false);

  // De velden volgen de server als wat geldt veranderde (na een opslag van
  // deze kaart, of van iemand anders) — niet bij een nieuwe ronde waarin deze
  // kaart gelijk bleef: dan blijft getypte, nog niet opgeslagen tekst staan.
  useEffect(() => {
    setTekst(beginTekst(item));
    setGeenDoel(item.magLeeg && item.waarde === null);
    setMotivering("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen als wat geldt veranderde
  }, [item.waarde, item.overschreven, item.op]);

  // De knoppen zetten zichzelf niet uit (aria-disabled), dus wie op Opslaan
  // klikte, houdt de focus. Eén knop verdwijnt wel: "Terug naar standaard" is
  // er na het terugzetten niet meer, en dan valt de focus op de pagina. Zodra
  // de kaart klaar is, krijgt Opslaan hem — ook als die dicht is omdat het
  // veld leeg bleef (een krediet zonder standaard): een aria-disabled knop kan
  // de focus wél dragen. Wie intussen elders stond, houdt zijn plek.
  // Een layout-effect: de focus staat er in dezelfde weergave waarin de kaart
  // klaar is en de melding klinkt. Een gewoon effect liep daar soms achteraan
  // (de CI van ronde 6 zag de focus nog op de pagina).
  useLayoutEffect(() => {
    if (bezig || !terugFocus.current) return;
    terugFocus.current = false;
    if (focusKwijt()) opslaanKnop.current?.focus();
  }, [bezig]);

  // Geen eigen bereikcontrole (zie de kop): alleen "staat er een getal".
  const inVeld = leesGetal(tekst);
  const geldig = inVeld !== null;
  const kan = !uit && (geenDoel || geldig);
  const waarom = motivering.trim() || null;
  // Wat er verstuurd wordt, alleen als er iets anders staat dan wat geldt: zes
  // kaarten met "Wordt opgeslagen als" hun eigen waarde zegt niemand iets.
  const toonWordt = !geenDoel && geldig && tekst.trim() !== beginTekst(item);

  async function stuur(body: InstellingRequest, maakMelding: (nieuw: Instelling) => string) {
    setFout(null);
    terugFocus.current = true;
    setFout(await onOpslaan(item, body, maakMelding));
  }

  function verstuur(e: FormEvent) {
    e.preventDefault();
    if (!kan) return;
    void stuur(
      { sleutel: item.sleutel, waarde: geenDoel ? null : inVeld, motivering: waarom },
      (nieuw) =>
        nieuw.waarde === null
          ? `Opgeslagen: ${naam} — ${leegWoord(nieuw)}.`
          : `Opgeslagen: ${naam} is nu ${inEenheid(nieuw.waarde, nieuw.eenheid)}.`,
    );
  }

  function terug() {
    if (uit) return;
    void stuur(
      // Een motivering gaat alleen mee als er een getypt is.
      { sleutel: item.sleutel, terugNaarStandaard: true, ...(waarom ? { motivering: waarom } : {}) },
      (nieuw) =>
        nieuw.waarde === null
          ? `Teruggezet naar de standaard: ${naam} — ${leegWoord(nieuw)}.`
          : `Teruggezet naar de standaard: ${naam} is weer ${inEenheid(nieuw.waarde, nieuw.eenheid)}.`,
    );
  }

  // Dollars, procenten of een aantal van een kredietsoort ("gesprekken"): het
  // woord naast het veld, zodat niemand $ en % door elkaar haalt.
  const voor = item.eenheid === "usd" ? "$" : null;
  const na =
    item.eenheid === "procent"
      ? "%"
      : item.eenheid === "aantal"
        ? item.soort
          ? kredietLabel(item.soort).toLowerCase()
          : "aantal"
        : null;
  const wie = herkomst(item);

  return (
    <li data-instelling={item.sleutel} aria-busy={bezig || undefined}>
      <Card
        style={{
          padding: "16px 18px",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
          <h3 style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink, margin: 0 }}>
            {naam}
          </h3>
          {item.overschreven && (
            <ReasonBadge
              text="overschreven"
              tone="blue"
              title="Deze waarde gaat voor de standaard (yaml)."
            />
          )}
          {item.concept && (
            <ReasonBadge
              text="concept"
              tone="amber"
              title="Nog een conceptgetal — de owner stelt het vast."
            />
          )}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 28, alignItems: "flex-end" }}>
          <div data-geldt>
            <div style={{ ...sectionLabel, marginBottom: 6 }}>Geldt nu</div>
            {item.waarde !== null ? (
              <div
                style={{
                  fontSize: 25,
                  fontWeight: weight.bold,
                  color: color.ink,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  ...cijfer,
                }}
              >
                {inEenheid(item.waarde, item.eenheid)}
              </div>
            ) : (
              <EmptyReason reason={leegWoord(item)} style={{ fontSize: size.body }} />
            )}
          </div>
          <div data-standaard>
            <div style={{ ...sectionLabel, marginBottom: 6 }}>Standaard (yaml)</div>
            {item.yamlWaarde !== null ? (
              <div style={{ fontSize: 16, color: color.body, ...cijfer }}>
                {inEenheid(item.yamlWaarde, item.eenheid)}
              </div>
            ) : (
              <EmptyReason reason={leegWoord(item)} />
            )}
          </div>
        </div>

        {wie ? (
          <p style={{ fontSize: 12, color: color.soft, margin: 0, lineHeight: 1.5 }}>
            {wie}
            {item.motivering && (
              <span style={{ color: color.body }}>{` “${item.motivering}”`}</span>
            )}
          </p>
        ) : (
          <EmptyReason reason="nog nooit gewijzigd — de standaard geldt" style={{ fontSize: 12 }} />
        )}

        {item.uitleg ? (
          <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.5, margin: 0 }}>
            {item.uitleg}
          </p>
        ) : (
          <EmptyReason reason="geen uitleg bij deze instelling" />
        )}

        <form
          noValidate
          onSubmit={verstuur}
          aria-label={`${naam} wijzigen`}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: "auto",
            paddingTop: 10,
            borderTop: `1px solid ${color.lineFaint}`,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {voor && (
                <span aria-hidden style={eenheidStijl}>
                  {voor}
                </span>
              )}
              {/* Tekst, geen getalveld: dat las "1.000" als 1 en "12,5" soms
                  als leeg (zie de kop). `inputMode` geeft op een telefoon
                  toch het cijferklavier. Tijdens een opslag alleen-lezen, niet
                  uit: zo houdt wie op Enter drukte de focus. */}
              {item.eenheid === "aanuit" ? (
                // Een schermmelding aan of uit (testfeedback 03-11): een keuze,
                // geen getalveld. De server bewaart 1 of 0.
                <select
                  id={`${id}-waarde`}
                  value={tekst === "1" ? "1" : "0"}
                  aria-disabled={uit || undefined}
                  onChange={(e) => {
                    if (uit) return;
                    setTekst(e.target.value);
                    setFout(null);
                  }}
                  aria-label={`Nieuwe waarde voor ${naam}`}
                  aria-describedby={`${id}-hint`}
                  style={{ ...invoer, width: 130 }}
                >
                  <option value="1">Aan</option>
                  <option value="0">Uit</option>
                </select>
              ) : (
              <input
                id={`${id}-waarde`}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={tekst}
                disabled={geenDoel}
                readOnly={uit}
                onChange={(e) => {
                  setTekst(e.target.value);
                  setFout(null);
                }}
                aria-label={`Nieuwe waarde voor ${naam}`}
                aria-invalid={!geenDoel && !geldig}
                aria-describedby={toonWordt ? `${id}-wordt ${id}-hint` : `${id}-hint`}
                style={{ ...invoer, width: 130 }}
              />
              )}
              {na && (
                <span aria-hidden style={eenheidStijl}>
                  {na}
                </span>
              )}
            </span>
            {toonWordt && inVeld !== null && (
              <span id={`${id}-wordt`} data-wordt style={{ fontSize: 12, color: color.body }}>
                {`Wordt opgeslagen als ${precies(inVeld, item)}`}
              </span>
            )}
            {item.magLeeg && (
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: size.meta,
                  color: color.body,
                }}
              >
                {/* Een vinkje heeft geen alleen-lezen: aria-disabled, en een
                    wijziging tijdens een opslag gaat niet door (React zet het
                    vinkje dan terug). */}
                <input
                  type="checkbox"
                  checked={geenDoel}
                  aria-disabled={uit || undefined}
                  onChange={(e) => {
                    if (uit) return;
                    setGeenDoel(e.target.checked);
                    setFout(null);
                  }}
                />
                Geen doel
              </label>
            )}
          </div>
          <input
            type="text"
            value={motivering}
            maxLength={2000}
            readOnly={uit}
            onChange={(e) => setMotivering(e.target.value)}
            placeholder="Waarom? (mag leeg blijven)"
            aria-label={`Motivering bij ${naam}`}
            style={{ ...invoer, padding: "7px 10px", fontSize: size.meta }}
          />
          {/* Niet alleen rood: de zin zelf zegt wat er moet gebeuren. Het bereik
              is dat van de server — een hint, geen controle. */}
          <p
            id={`${id}-hint`}
            style={{
              fontSize: 12,
              margin: 0,
              color: !geenDoel && !geldig ? color.accentRed : color.faint,
            }}
          >
            {geenDoel
              ? "Opslaan zet deze waarde op geen doel."
              : item.eenheid === "aanuit"
                ? "Kies aan of uit en klik op Opslaan."
                : !geldig
                ? "Vul een getal in."
                : `Van ${inEenheid(item.min, item.eenheid)} tot en met ${inEenheid(item.max, item.eenheid)}.`}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {/* aria-disabled, niet disabled (zie de kop): verstuur() en
                terug() houden zelf tegen wat nu niet kan. */}
            <button
              ref={opslaanKnop}
              type="submit"
              aria-disabled={!kan}
              style={{ ...knopDonker(!kan), display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <VinkIcoon /> {bezig ? "bezig…" : "Opslaan"}
            </button>
            {item.overschreven && (
              <button
                type="button"
                onClick={terug}
                aria-disabled={uit}
                style={{
                  ...knopLicht,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: uit ? color.faint : color.body,
                  cursor: uit ? "default" : "pointer",
                }}
              >
                <TerugIcoon /> Terug naar standaard
              </button>
            )}
          </div>
          {fout && (
            <p role="alert" style={{ fontSize: size.meta, color: color.accentRed, margin: 0 }}>
              {fout}
            </p>
          )}
        </form>
      </Card>
    </li>
  );
}

function LaatstGewijzigd({
  recent,
  instellingen,
}: {
  recent: readonly Recent[];
  instellingen: readonly Instelling[];
}) {
  return (
    <Blok titel="Laatst gewijzigd">
      {recent.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason reason="nog niets vastgesteld in dit scherm — overal geldt de standaard" />
        </Card>
      ) : (
        <Tabel label="Laatst gewijzigde instellingen" minBreedte={680}>
          <thead>
            <tr>
              {["Instelling", "Waarde", "Door", "Op", "Motivering"].map((k) => (
                <th key={k} scope="col" style={kopCel()}>
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recent.map((r, i) => (
              <RecentRij key={`${i}-${r.sleutel}`} r={r} instellingen={instellingen} />
            ))}
          </tbody>
        </Tabel>
      )}
    </Blok>
  );
}

function RecentRij({ r, instellingen }: { r: Recent; instellingen: readonly Instelling[] }) {
  // De eenheid staat bij de instelling zelf; een regel van iets wat niet meer
  // bestaat heeft er geen, en toont dan het kale getal.
  const eenheid = instellingen.find((x) => x.sleutel === r.sleutel)?.eenheid ?? null;
  return (
    <tr data-recent>
      <td style={cel()}>
        {/* De naam is altijd die van de server, ook voor iets wat niet meer
            bestaat ("Standaardkrediet van een rol die niet meer bestaat"),
            met de kredietsoort erbij. Zonder groep zegt de tweede regel
            waarom: dezelfde woorden als het blok eronder. */}
        <div style={{ color: color.ink }}>{naamVan(r)}</div>
        {r.groepLabel === null ? (
          <EmptyReason reason="hoort nergens meer bij" style={{ fontSize: 12 }} />
        ) : (
          <div style={klein}>{r.groepLabel}</div>
        )}
      </td>
      <td style={cel()}>
        {r.terugGezet
          ? "terug naar de standaard"
          : r.waarde === null
            ? "geen doel"
            : eenheid
              ? inEenheid(r.waarde, eenheid)
              : getal(r.waarde)}
      </td>
      <td style={cel()}>{r.door ?? <EmptyReason reason="niet vastgelegd" />}</td>
      <td style={{ ...cel(), whiteSpace: "nowrap" }}>
        {datum(r.op) ?? <EmptyReason reason="geen datum" />}
      </td>
      <td style={cel()}>{r.motivering ?? <EmptyReason reason="geen motivering" />}</td>
    </tr>
  );
}

function Verweesd({ rijen }: { rijen: ConfiguratieResponse["verweesd"] }) {
  return (
    <Blok titel="Hoort nergens meer bij">
      <Card style={{ padding: "14px 18px" }}>
        {rijen.length === 0 ? (
          <EmptyReason reason="geen — elke vastgelegde waarde hoort bij een bestaande instelling" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {rijen.map((r, i) => (
              <li
                key={r.sleutel}
                data-verweesd
                style={{
                  padding: "8px 0",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                }}
              >
                {/* De naam komt van de server (de sleutel zelf tonen we niet),
                    met de kredietsoort in ons woord, dan de waarde en waarom
                    ze niets doet. */}
                <span style={{ color: color.ink, fontWeight: weight.medium }}>{naamVan(r)}</span>
                <div style={{ color: color.body, marginTop: 3 }}>
                  {r.waarde !== null ? `Vastgelegde waarde ${getal(r.waarde)}` : "Vastgelegd zonder waarde"}
                </div>
                <div style={{ fontSize: 12, color: color.soft, marginTop: 3, lineHeight: 1.5 }}>
                  {r.reden}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Blok>
  );
}

function NietInstelbaar({ rijen }: { rijen: ConfiguratieResponse["nietInstelbaar"] }) {
  return (
    <Blok titel="Niet instelbaar">
      <Card style={{ padding: "14px 18px" }}>
        {rijen.length === 0 ? (
          <EmptyReason reason="de server noemt niets wat hier niet instelbaar is" />
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {rijen.map((r, i) => (
              <li
                key={r.wat}
                data-niet-instelbaar
                style={{
                  padding: "8px 0",
                  borderTop: i === 0 ? "none" : `1px solid ${color.lineFaint}`,
                  fontSize: size.meta,
                }}
              >
                <span style={{ color: color.ink, fontWeight: weight.medium }}>{r.wat}</span>
                <div style={{ fontSize: 12, color: color.soft, marginTop: 3, lineHeight: 1.5 }}>
                  {r.reden}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Blok>
  );
}

const eenheidStijl = { fontSize: size.meta, color: color.soft, whiteSpace: "nowrap" } as const;
